import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import {
  FaKey,
  FaShieldAlt,
  FaSpinner,
  FaCheckCircle,
} from 'react-icons/fa';

import api from '../services/api';
import { useAuth } from '../context/AuthContext';

import './Auth.css';

const Verify2FA = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { login } = useAuth();

  const mobile = location.state?.mobile || '';

  const [code, setCode] = useState([
    '',
    '',
    '',
    '',
    '',
    '',
  ]);

  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);

  const codeRefs = useRef([]);

  const codeValue = code.join('');

  /* =====================================================
     VERIFY 2FA
  ===================================================== */

  const verify = async () => {
    if (loading) return;

    /* ================= MOBILE CHECK ================= */

    if (!mobile) {
      toast.error(
        'Mobile number is missing. Please login again.'
      );

      navigate('/login', {
        replace: true,
      });

      return;
    }

    /* ================= CODE CHECK ================= */

    if (!/^\d{6}$/.test(codeValue)) {
      toast.error(
        'Enter a valid 6-digit code.'
      );

      return;
    }

    try {
      setLoading(true);

      console.log(
        'VERIFY 2FA REQUEST =>',
        {
          mobile,
          code: codeValue,
        }
      );

      /* =================================================
         VERIFY 2FA
      ================================================= */

      const res = await api.post(
        '/api/auth/verify-2fa',
        {
          mobile,
          code: parseInt(codeValue, 10),
        }
      );

      console.log(
        'VERIFY 2FA RESPONSE =>',
        res.data
      );

      /* =================================================
         VERIFICATION FAILED
      ================================================= */

      if (!res.data?.success) {
        toast.error(
          res.data?.message ||
            'Invalid authenticator code.'
        );

        return;
      }

      /* =================================================
         JWT REQUIRED
      ================================================= */

      if (!res.data?.token) {
        toast.error(
          'Authentication token not received.'
        );

        return;
      }

      /* =================================================
         CORPORATOR ONLY
      ================================================= */

      if (
        res.data?.role &&
        res.data.role !==
          'ROLE_CORPORATOR'
      ) {
        toast.error(
          'Invalid account type.'
        );

        return;
      }

      /* =================================================
         USER DATA
      ================================================= */

      const userData =
        res.data?.user || {
          id: res.data?.userId,
          role:
            res.data?.role ||
            'ROLE_CORPORATOR',
          mobile,
        };

      /* =================================================
         SAVE JWT
      ================================================= */

      await login(
        res.data.token,
        userData
      );

      setVerified(true);

      toast.success(
        '2FA verification successful.'
      );

      /* =================================================
         DASHBOARD
      ================================================= */

      setTimeout(() => {
        navigate('/dashboard', {
          replace: true,
        });
      }, 900);

    } catch (error) {
      console.log(
        'VERIFY 2FA ERROR =>',
        error?.response?.data ||
          error?.message ||
          error
      );

      toast.error(
        error?.response?.data?.message ||
          'Invalid authenticator code.'
      );

    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     CODE INPUT
  ===================================================== */

  const handleChange = (
    index,
    value
  ) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    const next = [...code];

    next[index] = value;

    setCode(next);

    if (
      value &&
      index < 5
    ) {
      codeRefs.current[
        index + 1
      ]?.focus();
    }
  };

  /* =====================================================
     BACKSPACE / ENTER
  ===================================================== */

  const handleKeyDown = (
    index,
    e
  ) => {
    if (
      e.key === 'Backspace' &&
      !code[index] &&
      index > 0
    ) {
      codeRefs.current[
        index - 1
      ]?.focus();
    }

    if (e.key === 'Enter') {
      verify();
    }
  };

  /* =====================================================
     PASTE
  ===================================================== */

  const handlePaste = (e) => {
    const text =
      e.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 6);

    if (!text) return;

    e.preventDefault();

    setCode(
      Array.from(
        { length: 6 },
        (_, index) =>
          text[index] || ''
      )
    );

    codeRefs.current[
      Math.min(
        text.length,
        5
      )
    ]?.focus();
  };

  /* =====================================================
     UI
  ===================================================== */

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F7F4EE',
        fontFamily:
          "'Inter', system-ui, sans-serif",
        padding: '24px',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;700&display=swap');

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }

          to {
            transform: rotate(360deg);
          }
        }

        @keyframes stampIn {
          0% {
            transform: scale(2.2) rotate(-8deg);
            opacity: 0;
          }

          60% {
            transform: scale(0.92) rotate(2deg);
            opacity: 1;
          }

          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }

        .code-box:focus {
          outline: none;
          border-color: #C9A227 !important;
          box-shadow:
            0 0 0 3px
            rgba(201,162,39,0.18);
        }

        .v2fa-btn:not(:disabled):hover {
          background: #0F2545 !important;
        }

        @media (prefers-reduced-motion: reduce) {
          .stamp {
            animation: none !important;
          }
        }
      `}</style>

      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          background: '#FFFFFF',
          borderRadius: '18px',
          overflow: 'hidden',
          boxShadow:
            '0 24px 60px -20px rgba(16,35,63,0.35)',
          padding: '44px 40px',
          textAlign: 'center',
        }}
      >
        {!verified ? (
          <>
            {/* =================================================
                ICON
            ================================================= */}

            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background:
                  'linear-gradient(160deg, #16325C 0%, #0F2545 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                border:
                  '1.5px solid #C9A227',
              }}
            >
              <FaShieldAlt
                size={24}
                color="#C9A227"
              />
            </div>

            {/* =================================================
                TITLE
            ================================================= */}

            <p
              style={{
                color: '#C9A227',
                fontSize: 11.5,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontWeight: 600,
                margin: '0 0 6px',
              }}
            >
              Step 2 of 2
            </p>

            <h1
              style={{
                fontFamily:
                  "'Fraunces', serif",
                fontWeight: 600,
                fontSize: 24,
                color: '#10233F',
                margin: '0 0 10px',
              }}
            >
              Two-Factor Authentication
            </h1>

            <p
              style={{
                color: '#697386',
                fontSize: 14,
                lineHeight: 1.6,
                margin: '0 0 8px',
              }}
            >
              Open Google Authenticator
              and enter the 6-digit code
              for your account.
            </p>

            {mobile && (
              <p
                style={{
                  color: '#9AA5B6',
                  fontSize: 12.5,
                  marginBottom: 28,
                }}
              >
                Verifying access for +91{' '}
                {mobile}
              </p>
            )}

            {/* =================================================
                OTP BOXES
            ================================================= */}

            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'center',
              }}
              onPaste={handlePaste}
            >
              {code.map(
                (digit, index) => (
                  <input
                    key={index}
                    ref={(el) =>
                      (codeRefs.current[
                        index
                      ] = el)
                    }
                    className="code-box"
                    value={digit}
                    onChange={(e) =>
                      handleChange(
                        index,
                        e.target.value
                      )
                    }
                    onKeyDown={(e) =>
                      handleKeyDown(
                        index,
                        e
                      )
                    }
                    inputMode="numeric"
                    maxLength={1}
                    disabled={loading}
                    autoFocus={
                      index === 0
                    }
                    style={{
                      width: 44,
                      height: 52,
                      textAlign:
                        'center',
                      fontFamily:
                        "'JetBrains Mono', monospace",
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#10233F',
                      border:
                        '1.5px solid #DDE2EA',
                      borderRadius: 10,
                      background:
                        '#FCFBF8',
                    }}
                  />
                )
              )}
            </div>

            {/* =================================================
                VERIFY BUTTON
            ================================================= */}

            <button
              className="v2fa-btn"
              onClick={verify}
              disabled={loading}
              style={{
                marginTop: 26,
                width: '100%',
                background: '#16325C',
                color: '#F7F4EE',
                border: 'none',
                borderRadius: 10,
                padding: '13px 0',
                fontSize: 14.5,
                fontWeight: 600,
                cursor: loading
                  ? 'default'
                  : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: loading
                  ? 0.75
                  : 1,
              }}
            >
              {loading ? (
                <>
                  <FaSpinner
                    size={14}
                    style={{
                      animation:
                        'rotate 0.8s linear infinite',
                    }}
                  />
                  Verifying
                </>
              ) : (
                <>
                  <FaKey size={13} />
                  Verify
                </>
              )}
            </button>
          </>
        ) : (
          /* =================================================
             SUCCESS
          ================================================= */

          <div
            className="stamp"
            style={{
              animation:
                'stampIn 0.5s ease-out',
              padding: '20px 0',
            }}
          >
            <FaCheckCircle
              size={44}
              color="#1F7A4D"
              style={{
                marginBottom: 14,
              }}
            />

            <h2
              style={{
                fontFamily:
                  "'Fraunces', serif",
                fontSize: 22,
                color: '#10233F',
                margin: '0 0 6px',
              }}
            >
              Verified
            </h2>

            <p
              style={{
                color: '#697386',
                fontSize: 14,
              }}
            >
              Taking you to your
              dashboard…
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Verify2FA;